import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ContentBlock } from '../../types';

import { colorAt, normalizeChart } from './format';

const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 11 } as const;

function formatTick(asPercent: boolean, unit?: string) {
  return (v: number) => {
    if (asPercent) return `${(v * 100).toFixed(0)}%`;
    if (unit === '$') return `$${v.toLocaleString()}`;
    return v.toLocaleString();
  };
}

export function ChartBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const chart = normalizeChart(block.payload ?? {}, block.metadata ?? {});
  const title = typeof block.metadata?.title === 'string' ? block.metadata.title : undefined;

  const hasData =
    chart.kind === 'pie' || chart.kind === 'donut' ? chart.pie.length > 0 : chart.data.length > 0;

  return (
    <figure className="rounded-lg border bg-card p-4">
      {title && (
        <figcaption className="mb-3 text-sm font-medium text-foreground">{title}</figcaption>
      )}
      {!hasData ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t('block.noData', 'No data')}
        </p>
      ) : (
        <div dir="ltr">
          <ResponsiveContainer width="100%" height={260}>
            {renderChart(chart)}
          </ResponsiveContainer>
        </div>
      )}
    </figure>
  );
}

function renderChart(chart: ReturnType<typeof normalizeChart>) {
  const tickFmt = formatTick(chart.asPercent, chart.unit);

  if (chart.kind === 'pie' || chart.kind === 'donut') {
    const total = chart.pie.reduce((sum, s) => sum + s.value, 0) || 1;
    return (
      <PieChart>
        <Pie
          data={chart.pie}
          dataKey="value"
          nameKey="name"
          innerRadius={chart.kind === 'donut' ? 60 : 0}
          outerRadius={90}
          paddingAngle={1}
        >
          {chart.pie.map((slice, i) => (
            <Cell key={slice.name} fill={slice.color ?? colorAt(i)} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number) => `${((value / total) * 100).toFixed(1)}%`}
          contentStyle={tooltipStyle}
        />
        <Legend />
      </PieChart>
    );
  }

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
      <XAxis dataKey={chart.xKey} tick={AXIS_TICK} tickLine={false} axisLine={false} />
      <YAxis
        tick={AXIS_TICK}
        tickLine={false}
        axisLine={false}
        tickFormatter={tickFmt}
        width={48}
      />
      <Tooltip
        formatter={(value: number) => (chart.asPercent ? `${(value * 100).toFixed(2)}%` : value)}
        contentStyle={tooltipStyle}
      />
      {chart.series.length > 1 && <Legend />}
    </>
  );

  if (chart.kind === 'line') {
    return (
      <LineChart data={chart.data} margin={CHART_MARGIN}>
        {axes}
        {chart.series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color ?? colorAt(i)}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    );
  }

  if (chart.kind === 'area') {
    return (
      <AreaChart data={chart.data} margin={CHART_MARGIN}>
        {axes}
        {chart.series.map((s, i) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color ?? colorAt(i)}
            fill={s.color ?? colorAt(i)}
            fillOpacity={0.15}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    );
  }

  // bar (default)
  return (
    <BarChart data={chart.data} margin={CHART_MARGIN}>
      {axes}
      {chart.series.map((s, i) => (
        <Bar
          key={s.key}
          dataKey={s.key}
          name={s.label}
          fill={s.color ?? colorAt(i)}
          radius={[3, 3, 0, 0]}
        />
      ))}
    </BarChart>
  );
}

const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: -8 } as const;

const tooltipStyle = {
  background: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  fontSize: 12,
  color: 'hsl(var(--popover-foreground))',
} as const;
